/**
 * Auth Error Modal
 * Modal explaining email account authentication issues and providing link to settings
 */
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router";
import { Button } from "@/components/base/buttons/button";
import { ModalOverlay, Modal, Dialog } from "@/components/application/modals/modal";
import { X, AlertCircle, Settings01 } from "@untitledui/icons";
import type { EmailAccountResponse } from "@/types/api/email";

const PROFILE_ROLE_SEGMENTS = ["shipper", "receiver", "admin", "freight-forwarder"] as const;

function getProfilePathFromLocation(pathname: string): string {
    const segment = pathname.split("/").filter(Boolean)[0];
    const role = segment && PROFILE_ROLE_SEGMENTS.includes(segment as (typeof PROFILE_ROLE_SEGMENTS)[number])
        ? segment
        : "shipper";
    return `/${role}/profile?tab=integrations`;
}

interface AuthErrorModalProps {
    isOpen: boolean;
    onClose: () => void;
    account: EmailAccountResponse | null;
}

export const AuthErrorModal = ({
    isOpen,
    onClose,
    account,
}: AuthErrorModalProps) => {
    const { t } = useTranslation(["pages:email-settings"]);
    const navigate = useNavigate();
    const { pathname } = useLocation();

    if (!account) return null;

    const handleGoToSettings = () => {
        navigate(getProfilePathFromLocation(pathname));
        onClose();
    };

    return (
        <ModalOverlay isOpen={isOpen} onOpenChange={(open) => !open && onClose()}>
            <Modal className="max-w-md">
                <Dialog>
                    <div className="w-full rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                        {/* Header */}
                        <div className="mb-6 flex items-center justify-between">
                            <h2 className="text-lg font-semibold text-primary">
                                {t("authError.title", { defaultValue: "Autenticazione Richiesta" })}
                            </h2>
                            <Button
                                onClick={onClose}
                                color="tertiary"
                                size="sm"
                                iconLeading={X}
                                aria-label="Close"
                            />
                        </div>

                        {/* Icon */}
                        <div className="mb-4 flex justify-center">
                            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-error-secondary">
                                <AlertCircle className="h-6 w-6 text-error-primary" />
                            </div>
                        </div>

                        {/* Account Email */}
                        <div className="mb-4 text-center">
                            <p className="text-sm font-medium text-primary">{account.email_address}</p>
                        </div>

                        {/* Description */}
                        <div className="mb-6 space-y-3">
                            <p className="text-sm text-tertiary text-center">
                                {t("authError.description", { 
                                    defaultValue: "Il token di autenticazione per questo account email è scaduto o è stato revocato." 
                                })}
                            </p>
                            <p className="text-sm text-tertiary text-center">
                                {t("authError.instruction", { 
                                    defaultValue: "Per continuare a sincronizzare le email, è necessario riautenticare l'account nelle impostazioni." 
                                })}
                            </p>
                            {account.last_error && (
                                <div className="mt-4 rounded-lg bg-error-secondary/20 p-3">
                                    <p className="text-xs text-error-primary font-mono break-words">
                                        {account.last_error}
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Actions */}
                        <div className="flex flex-col gap-2">
                            <Button
                                onClick={handleGoToSettings}
                                color="primary"
                                size="sm"
                                iconLeading={Settings01}
                            >
                                {t("authError.goToSettings", { defaultValue: "Vai alle Impostazioni Email" })}
                            </Button>
                            <Button
                                onClick={onClose}
                                color="secondary"
                                size="sm"
                            >
                                {t("authError.close", { defaultValue: "Chiudi" })}
                            </Button>
                        </div>
                    </div>
                </Dialog>
            </Modal>
        </ModalOverlay>
    );
};
